import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, Resolve, RouterStateSnapshot } from '@angular/router';

import { OfferSource } from '@cds';
import { OfferMapping } from '@cds/query-objects';
import { DetailedCreateFactory, DetailedGroupingFactory } from '@frontend/sports/betting-offer/feature/fixture-factories';
import { EnhancedFixtureViewGroupingConfiguration } from '@frontend/sports/betting-offer/feature/model';
import { OfferGroupingService } from '@frontend/sports/betting-offer/feature/offer-grouping';
import { BettingOfferApi } from '@frontend/sports/betting-offer/feature/offer-service';
import { Sitecore } from '@frontend/sports/common/client-config-data-access';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { StylesLazyLoadingService } from '@frontend/sports/deferred/feature/styles-loader';
import { WinGameFixture, toWinGameFixture } from '@frontend/sports/racing/feature/models';
import { firstValueFrom, map } from 'rxjs';

export interface AntepostFixtureModel {
    fixture: Nullable<WinGameFixture>;
    title: string;
}

@Injectable()
export class AntepostFixtureResolver implements Resolve<AntepostFixtureModel> {
    grouping: EnhancedFixtureViewGroupingConfiguration | undefined;

    constructor(
        private apiService: BettingOfferApi,
        private sitecore: Sitecore,
        private stylesService: StylesLazyLoadingService,
        protected createFactory: DetailedCreateFactory,
        protected groupingFactory: DetailedGroupingFactory,
        protected offerGroupingService: OfferGroupingService,
    ) {
        this.getgrouping(SportConstant.Horses);
    }

    async resolve(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Promise<AntepostFixtureModel> {
        const title =
            +route.params.sport === SportConstant.Horses
                ? this.sitecore.horseRacing.navigation.FutureAndSpecials
                : this.sitecore.fixture.common.EachWayMarkets;
        const empty: AntepostFixtureModel = { fixture: null, title };

        const data = firstValueFrom(
            this.apiService
                .getFixtureList({
                    fixtureIds: route.params.fixture,
                    offerSource: OfferSource.V2,
                    offerMapping: OfferMapping.All,
                })
                .pipe(
                    map((fixturePage) => {
                        const antepostFixture =
                            !!fixturePage && fixturePage.fixtures.length === 1
                                ? { fixture: toWinGameFixture(fixturePage.fixtures[0]), title }
                                : empty;
                        if (antepostFixture.fixture && fixturePage && this.grouping) {
                            antepostFixture.fixture.optionGroups = this.createFactory.createOptionGroups(
                                fixturePage.fixtures[0],
                                [],
                                this.grouping,
                                undefined,
                                false,
                            );
                            antepostFixture.fixture.optionSets = this.groupingFactory.getOptionSets(
                                antepostFixture.fixture.optionGroups,
                                this.grouping,
                                fixturePage.fixtures[0].source,
                            );
                        }

                        return antepostFixture;
                    }),
                ),
        );

        const [model] = await Promise.all([data, this.stylesService.lazyLoad('racing')]);

        return model;
    }

    private async getgrouping(sportId: number) {
        this.grouping = await firstValueFrom(this.offerGroupingService.getFixtureGrouping(sportId, 'any'));
    }
}
